import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { ProductCard as Card } from '@/lib/api';
import { Availability } from './Availability';
import { Price } from './Price';
import { SwatchRail } from './Swatch';
import { useLocalePath } from '@/hooks/useLocalePath';
import type { JSX } from 'react';

/**
 * Image-first with a swatch rail beneath. No card, no shadow, no rule — the product sits
 * on the ground and the photography does the work.
 */
/**
 * `feature` is the merchandised size: a bigger frame, a bigger name, and room to breathe.
 * `compact` is the opposite end. The point is that a homepage that renders every product
 * at one size tells a customer nothing about which product the shop is actually leading
 * with — hierarchy is the merchandising.
 */
export type CardSize = 'feature' | 'default' | 'compact';

const CARD: Record<CardSize, { frame: string; title: string; sizes: string }> = {
  feature: { frame: 'aspect-[4/5]', title: 'text-base md:text-lg', sizes: '(max-width: 1024px) 92vw, 46vw' },
  default: { frame: 'aspect-square', title: 'text-sm', sizes: '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 22vw' },
  compact: { frame: 'aspect-square', title: 'text-xs', sizes: '(max-width: 640px) 45vw, 16vw' },
};

export function ProductCard({
  product,
  index = 0,
  size = 'default',
}: {
  product: Card;
  index?: number;
  size?: CardSize;
}): JSX.Element {
  const { t } = useTranslation();
  const path = useLocalePath();

  return (
    <motion.article
      // Transform only, never opacity. A scroll-triggered reveal that fades in from 0
      // leaves every card below the fold invisible until its observer fires — 16 of 24
      // products on this page were blank to a fast scroll, a print, or a crawler. Sliding
      // a card that never animates costs 16px of position; fading one costs the product.
      initial={{ y: 16 }}
      whileInView={{ y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      // Staggered reveal, capped so a 24-product page does not take 3 seconds to appear.
      transition={{ duration: 0.45, delay: Math.min(index, 7) * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="group min-w-0 [&_a]:transition-transform [&_a]:duration-[var(--dur-hover)] [&_a]:ease-[var(--ease-spring)] hover:[&_a]:-translate-y-1 motion-reduce:hover:[&_a]:translate-y-0"
    >
      <Link to={path(`/produkt/${product.slug}`)} className="block focus-visible:outline-offset-4">
        {/* Square, because the photography is: 6112 of 6457 ingested images are 1:1. A 4:5 box
            letterboxed nearly all of them. */}
        <div className={`relative overflow-hidden bg-[var(--color-ground-lift)] ${CARD[size].frame}`}>
          {product.image?.card ? (
            <img
              src={product.image.card}
              srcSet={
                [
                  product.image.thumb ? `${product.image.thumb} 200w` : '',
                  product.image.card ? `${product.image.card} 640w` : '',
                  product.image.full ? `${product.image.full} 1600w` : '',
                ]
                  .filter(Boolean)
                  .join(', ') || undefined
              }
              sizes={CARD[size].sizes}
              alt={product.image.alt}
              loading={index < 4 ? 'eager' : 'lazy'}
              decoding="async"
              /* Explicit dimensions prevent layout shift on an image-heavy catalogue. */
              width={640}
              height={640}
              /* `contain`, not `cover`: supplier photography is shot at mixed aspect ratios
                 and a crop cuts legs off beds and arms off corner sofas. The letterboxing
                 is the honest trade — the customer sees the whole product. */
              className="h-full w-full object-contain p-2 transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.03]"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-[var(--color-muted)] text-xs">
              {t('common.noResults')}
            </div>
          )}
          {product.variantCount > 1 && (
            <span className="absolute left-3 top-3 bg-[var(--color-ground-lift)]/90 px-2 py-1 text-2xs font-semibold tracking-wide">
              {t('product.variantCount', { count: product.variantCount })}
            </span>
          )}
        </div>

        <div className="pt-3 space-y-2">
          <h3 className={`font-body font-semibold leading-snug tracking-normal ${CARD[size].title}`}>
            {product.name}
            {/* Untranslated fields fall back to the source language with a discreet marker. */}
            {product.nameIsFallback && (
              <span className="ml-1.5 align-middle text-2xs font-normal text-[var(--color-muted)]" title={t('product.untranslatedNote')}>
                ·pl
              </span>
            )}
          </h3>
          <SwatchRail colors={product.colors} />
          <div className="flex items-baseline justify-between gap-2">
            <Price
              minor={product.minPriceGross}
              from={product.maxPriceGross !== product.minPriceGross}
              className="font-semibold text-sm"
            />
            <Availability
              state={product.availability.state}
              quantity={product.availability.quantity}
              leadTimeDays={product.availability.leadTimeDays}
              compact
            />
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

/** Matches the card layout exactly, so nothing shifts when the data lands. */
export function ProductCardSkeleton(): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="rz-skeleton aspect-square" />
      <div className="rz-skeleton h-4 w-3/4" />
      <div className="rz-skeleton h-3 w-1/3" />
      <div className="rz-skeleton h-4 w-1/2" />
    </div>
  );
}
